// The device's copy of a lot after an offline status change, Move or Relist (BL-2448, ADR 066 —
// Offline Mode § 3): what the server will make of the lot, written onto the cached record so the
// offline screens show it before it syncs. Pure: the queued change and the cached lot in, the record out.
import type { OfflineInventoryListItemDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import type { LotDetailPayload } from '@auctogon/core-admin/schemas/responses/lot-detail';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

import type { OfflineNewLotPlacement } from './offline-new-lot';

/** The two statuses an offline status menu sets (BL-2437). */
export type OfflineStatusTarget = 'draft' | 'ready';

/** The edit lock of a Draft or Upcoming lot with no bids: every field open, movable. */
const PRE_OPEN_EDIT_LOCK: LotDetailPayload['editLock'] = {
  isSold: false,
  isActive: false,
  hasBids: false,
  isDraftOrReady: true,
  hasActivePreBids: false,
};

function withPhase(lot: OfflineDeltaLotDto, phase: OfflineStatusTarget): OfflineDeltaLotDto {
  return {
    ...lot,
    detail: { ...lot.detail, phaseKind: phase },
    row: { ...lot.row, lotPhaseKind: phase, status: phase },
  };
}

/**
 * Draft ↔ Upcoming on the device's copy: the phase the transition service will stamp.
 *
 * @param input.lot - the cached lot.
 * @param input.target - the status picked.
 * @returns the lot as the offline screens should now show it.
 */
export function applyLotStatusOverlay(input: { lot: OfflineDeltaLotDto; target: OfflineStatusTarget }): OfflineDeltaLotDto {
  return withPhase(input.lot, input.target);
}

/**
 * The auction fields a placement writes on the detail and the list row. The old auction's location
 * is cleared rather than kept: the device holds no copy of the new auction's in that shape, and the
 * next sync brings the server's (a new lot made offline starts with none too).
 */
function placed(placement: OfflineNewLotPlacement | null) {
  return {
    detail: {
      auctionId: placement?.auctionId ?? null,
      auctionName: placement?.auctionName ?? null,
      auctionTimeZone: placement?.auctionTimeZone ?? null,
      auctionLocation: null,
    },
    row: { auctionId: placement?.auctionId ?? undefined, auctionName: placement?.auctionName ?? undefined },
  };
}

/**
 * Move (or Remove from auction) on the device's copy, as `moveLotBetweenAuctionsTx` writes it:
 * the new auction, no lot number until the server numbers it, and Draft either way (BUG-2144 —
 * a move into another auction clears the lot's Ready stamp exactly like Remove from auction;
 * lifecycle sign-off, Michael Lawrie, 2026-10-16). `row.closeTime` is cleared too — `detail`
 * carries no close time at all (BL-2253, projected only by the live lot-state stream).
 *
 * @param input.lot - the cached lot.
 * @param input.placement - the auction it moves to, or null for Remove from auction.
 * @returns the lot as the offline screens should now show it.
 */
export function applyLotMoveOverlay(input: { lot: OfflineDeltaLotDto; placement: OfflineNewLotPlacement | null }): OfflineDeltaLotDto {
  const where = placed(input.placement);
  const moved: OfflineDeltaLotDto = {
    ...input.lot,
    detail: { ...input.lot.detail, ...where.detail, lotNumber: null, lotNumberSuffix: '' },
    row: { ...input.lot.row, ...where.row, lotNumber: null, closeTime: undefined },
  };
  return withPhase(moved, 'draft');
}

/**
 * A relist's copy as the device shows it until the relist syncs: the source's catalog, pricing,
 * seller and photos under the copy's client id, in the target auction (or Unassigned), Draft and
 * numberless, with nothing of the source's sale (bids, outcome, grouping, documents, notes). It
 * mirrors what `buildRelistInsertRow` copies on the server.
 *
 * @param input.source - the cached lot being relisted.
 * @param input.clientId - the relist's client id (its entry id and `clientOpId`).
 * @param input.placement - the target auction, or null for a relist back to inventory.
 * @param input.at - now, ISO 8601.
 * @returns the copy's record.
 */
export function buildOfflineRelistCopy(input: {
  source: OfflineDeltaLotDto;
  clientId: string;
  placement: OfflineNewLotPlacement | null;
  at: string;
}): OfflineDeltaLotDto {
  const { source, clientId, at } = input;
  const where = placed(input.placement);
  const lotCountForItem = source.detail.lotCountForItem + 1;
  const detail: LotDetailPayload = {
    ...source.detail,
    ...where.detail,
    id: clientId,
    lotNumber: null,
    lotNumberSuffix: '',
    phaseKind: 'draft',
    editLock: PRE_OPEN_EDIT_LOCK,
    lotCountForItem,
    parentLotId: null,
    parentLotNumberLabel: null,
    linkType: null,
    documents: [],
    titleMailingStatus: null,
    titleMailingTrackingNumber: null,
    titleMailingDateSent: null,
    titleMailingRecipientName: null,
    titleMailingRecipientAddress: null,
    createdAt: at,
    updatedAt: at,
    createdBy: null,
    updatedBy: null,
  };
  const row: OfflineInventoryListItemDto = {
    ...source.row,
    ...where.row,
    id: clientId,
    lotId: clientId,
    lotNumber: null,
    status: 'draft',
    lotPhaseKind: 'draft',
    lotDisposition: undefined,
    hammerPrice: null,
    closeTime: undefined,
    parentLotId: undefined,
    lotCountForItem,
    moveLock: { isDraftOrReady: true, hasBids: false, hasActivePreBids: false },
    createdAt: at,
    updatedAt: at,
  };
  return { detail, row, notes: [] };
}
